import React, { useEffect, useRef, useState } from "react";
import {
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TagTicketModal from "../TagTicketModal";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    labelRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: theme.spacing(0.5),
    },
    labelText: {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    addButton: {
        padding: 2,
    },
    chipsRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        minHeight: 28,
    },
    chip: {
        fontWeight: "bold",
        fontSize: "0.72em",
        borderRadius: 50,
        color: "#fff",
        height: 24,
    },
    select: {
        marginTop: 0,
    },
    emptyText: {
        fontSize: "0.78rem",
        color: theme.palette.text.disabled,
        fontStyle: "italic",
    },
}));

/**
 * TagsAtendimentoContainer
 *
 * Exibe as tags de atendimento (kanban=0) vinculadas ao ticket.
 * Permite vincular múltiplas tags e criar novas via TagTicketModal.
 */
export function TagsAtendimentoContainer({ ticket }) {
    const classes = useStyles();
    const isMounted = useRef(true);

    const [allTags, setAllTags] = useState([]);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [tagModalOpen, setTagModalOpen] = useState(false);

    useEffect(() => {
        isMounted.current = true;
        // Carrega tags e resolve seleção no mesmo ciclo — evita renders intermediários com estado parcial
        loadTags();

        return () => {
            isMounted.current = false;
        };
    }, [ticket?.id, ticket?.tags]);

    /**
     * Busca as tags de atendimento (kanban=0) da API e, no mesmo ciclo assíncrono,
     * cross-referencia com ticket.tags por ID para inicializar selectedTagIds.
     * Garante que allTags e selectedTagIds são definidos juntos → um único re-render correto.
     */
    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`, { params: { kanban: 0 } });
            if (!isMounted.current) return;

            setAllTags(data);

            // Cross-reference por ID: identifica quais tags de atendimento o ticket já tem
            // Não depende do campo `kanban` em ticket.tags (compatível com ticket de qualquer fonte)
            if (ticket?.tags && data.length > 0) {
                const atendTagIds = new Set(data.map(t => t.id));
                const matched = ticket.tags
                    .filter(t => atendTagIds.has(t.id))
                    .map(t => t.id);
                setSelectedTagIds(matched);
            } else {
                setSelectedTagIds([]);
            }
        } catch (err) {
            toastError(err);
        }
    };

    const handleAddTag = async (tagId) => {
        if (!tagId || selectedTagIds.includes(tagId)) return;
        try {
            await api.put(`/ticket-tags/${ticket.id}/${tagId}`);
            setSelectedTagIds((prev) => [...prev, tagId]);
        } catch (err) {
            toastError(err);
        }
    };

    const handleRemoveTag = async (tagId) => {
        try {
            await api.delete(`/ticket-tags/${ticket.id}/${tagId}`);
            setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
        } catch (err) {
            toastError(err);
        }
    };

    const handleTagModalClose = () => {
        setTagModalOpen(false);
        // Recarrega lista de tags após criar nova
        loadTags();
    };

    const availableTags = allTags.filter((t) => !selectedTagIds.includes(t.id));
    const selectedTags = allTags.filter((t) => selectedTagIds.includes(t.id));

    return (
        <div className={classes.root}>
            {/* Cabeçalho com label e botão criar tag */}
            <div className={classes.labelRow}>
                <Typography className={classes.labelText}>
                    Tags de Atendimento
                </Typography>
                <Tooltip title="Criar nova tag de atendimento" arrow>
                    <IconButton
                        size="small"
                        className={classes.addButton}
                        onClick={() => setTagModalOpen(true)}
                        color="primary"
                    >
                        <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>

            {/* Chips das tags já vinculadas */}
            <div className={classes.chipsRow}>
                {selectedTags.length === 0 ? (
                    <Typography className={classes.emptyText}>
                        Nenhuma tag vinculada
                    </Typography>
                ) : (
                    selectedTags.map((tag) => (
                        <Chip
                            key={tag.id}
                            label={tag.name}
                            size="small"
                            className={classes.chip}
                            style={{ backgroundColor: tag.color }}
                            onDelete={() => handleRemoveTag(tag.id)}
                        />
                    ))
                )}
            </div>

            {/* Select para adicionar nova tag */}
            {availableTags.length > 0 && (
                <FormControl fullWidth margin="dense" variant="outlined" className={classes.select}>
                    <InputLabel id="atend-tag-select-label">Adicionar tag</InputLabel>
                    <Select
                        labelId="atend-tag-select-label"
                        label="Adicionar tag"
                        value=""
                        onChange={(e) => {
                            if (e.target.value) handleAddTag(e.target.value);
                        }}
                        MenuProps={{
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                            getContentAnchorEl: null,
                        }}
                        renderValue={() => ""}
                    >
                        <MenuItem value="">
                            <em>Selecione uma tag...</em>
                        </MenuItem>
                        {availableTags.map((tag) => (
                            <MenuItem key={tag.id} value={tag.id}>
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor: tag.color,
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                {tag.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Modal para criar nova tag de atendimento — sem opção kanban */}
            <TagTicketModal
                open={tagModalOpen}
                onClose={handleTagModalClose}
                tagId={null}
                reload={loadTags}
                hideKanban={true}
            />
        </div>
    );
}
